/**
 * Pending completions store — delays task completion by ~2s with undo.
 *
 * v1 behaviour (useStatusChangeTimeout + markedForStatusChange):
 * - Ticking a task starts a 2s grace period; the checkbox fills and pulses.
 * - Ticking another task joins the same batch and restarts the grace period
 *   for the whole batch, and every pending checkbox's pulse restarts with it,
 *   so the batch pulses in step.
 * - Ticking a pending task again takes it out of the batch (undo).
 * - When the grace period runs out, the whole batch leaves the list at once.
 *
 * "At once" is done in the UI, not by the database: every row of the batch
 * collapses in the same frame (`.task-row-collapsing`, 400ms), and only then
 * are the completions written, one by one, behind the hidden rows. Before,
 * the rows stayed until each write reached the view's live query, so on a
 * slow device they could drop out one at a time.
 */

import { create } from "zustand"
import { completeTask, cancelTask } from "@/db/operations"
import { ObjectStatus } from "@/db/schema"
import { routeContainerStatus } from "@/features/project-header/projectStatusRequest"
import { useViewFilterStore } from "@/features/views/stores/viewFilterStore"

const COMPLETION_DELAY = 2000
/** Matches the `.task-row-collapsing` animation in globals.css. */
const COLLAPSE_MS = 400
/**
 * How long collapsed rows stay hidden after their writes. By then the view's
 * live query has dropped them; keeping them hidden until then stops a row from
 * flashing back into view between the write and the query update.
 */
const RELEASE_MS = 5000

type PendingAction = "complete" | "cancel"

interface PendingEntry {
  action: PendingAction
  timer: ReturnType<typeof setTimeout>
}

interface PendingCompletionsState {
  /** The open batch: taskId → pending entry (all share one timer) */
  pending: Map<string, PendingEntry>

  /** Batch whose grace period has run out and is being written */
  committing: Map<string, PendingAction>

  /** Set of taskIds currently collapsed out of the list */
  collapsing: Set<string>

  /**
   * Bumped whenever a task joins the open batch. Pulsing checkboxes restart
   * their animation when it changes, so the whole batch pulses in step.
   */
  batchEpoch: number

  /**
   * document.timeline time of the latest join. Every pulsing checkbox pins its
   * animation's startTime to it — restarting via currentTime = 0 left the
   * newly ticked one a frame or more behind, since its animation only starts
   * on the next frame.
   */
  batchStartTime: number | null

  /** Schedule a delayed completion/cancellation (or undo it if pending) */
  add: (taskId: string, action: PendingAction) => void

  /** Cancel a pending completion (undo) */
  remove: (taskId: string) => void

  /** Check if a task is pending completion */
  isPending: (taskId: string) => boolean

  /** Check if a task is in collapse animation phase */
  isCollapsing: (taskId: string) => boolean

  /** Get the pending action for a task */
  getAction: (taskId: string) => PendingAction | undefined

  /** Flush all pending completions immediately (no timer wait) */
  flushAll: () => Promise<void>
}

async function writeStatus(id: string, action: PendingAction): Promise<void> {
  try {
    // Safety net: a project / list that got into the queue is closed the container way (the dialog
    // asks what to do with its open to-dos); the task ops refuse it
    if (await routeContainerStatus(id, action === "complete" ? ObjectStatus.Completed : ObjectStatus.Canceled)) return
    const result =
      action === "complete"
        ? await completeTask(id, { skipUndo: true })
        : await cancelTask(id, { skipUndo: true })
    if (!result.ok) console.warn(`[pendingCompletions] ${action} failed:`, id, result.error)
  } catch (e) {
    console.error(`[pendingCompletions] ${action} error for task:`, id, e)
  }
}

export const usePendingCompletions = create<PendingCompletionsState>(
  (set, get) => {
    const commitBatch = async () => {
      const open = get().pending
      if (open.size === 0) return
      const batch = new Map([...open].map(([id, entry]) => [id, entry.action]))

      // Rows leave the list only if the view hides that status afterwards.
      // With "show completed" on they stay (checked), so don't collapse them.
      const { showCompleted, showCanceled } = useViewFilterStore.getState()
      const leaving = [...batch]
        .filter(([, action]) => (action === "complete" ? !showCompleted : !showCanceled))
        .map(([id]) => id)

      // One state update → every row of the batch collapses in the same frame.
      set((s) => ({
        pending: new Map(),
        committing: new Map([...s.committing, ...batch]),
        collapsing: new Set([...s.collapsing, ...leaving]),
      }))

      // flushAll() only drains the open batch. If the app is backgrounded
      // during this wait the timer resumes with it and the writes still run.
      if (leaving.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, COLLAPSE_MS))
      }
      for (const [id, action] of batch) await writeStatus(id, action)

      // Rows that stay in the list now show their real (written) status, so
      // stop treating them as pending right away.
      const leavingSet = new Set(leaving)
      const staying = [...batch.keys()].filter((id) => !leavingSet.has(id))
      const release = (ids: string[]) =>
        set((s) => {
          const committing = new Map(s.committing)
          const collapsing = new Set(s.collapsing)
          for (const id of ids) {
            committing.delete(id)
            collapsing.delete(id)
          }
          return { committing, collapsing }
        })
      if (staying.length > 0) release(staying)
      setTimeout(() => release(leaving), RELEASE_MS)
    }

    return {
      pending: new Map(),
      committing: new Map(),
      collapsing: new Set(),
      batchEpoch: 0,
      batchStartTime: null,

      add: (taskId, action) => {
        const { pending, committing, remove } = get()

        // Its grace period is over and it is being written — too late to undo.
        if (committing.has(taskId)) return

        // Already pending — take it out of the batch (undo)
        if (pending.has(taskId)) {
          remove(taskId)
          return
        }

        // Join the batch: restart the shared grace period so the whole batch
        // completes COMPLETION_DELAY after the LAST tick.
        for (const [, entry] of pending) clearTimeout(entry.timer)
        const timer = setTimeout(() => void commitBatch(), COMPLETION_DELAY)

        const next = new Map<string, PendingEntry>()
        for (const [id, entry] of pending) next.set(id, { action: entry.action, timer })
        next.set(taskId, { action, timer })

        const now = document.timeline?.currentTime
        set((s) => ({
          pending: next,
          batchEpoch: s.batchEpoch + 1,
          batchStartTime: typeof now === "number" ? now : null,
        }))
      },

      remove: (taskId) => {
        set((state) => {
          const entry = state.pending.get(taskId)
          if (!entry) return state
          const next = new Map(state.pending)
          next.delete(taskId)
          // Only clear the timer if this was the last pending task
          if (next.size === 0) {
            clearTimeout(entry.timer)
          }
          return { pending: next }
        })
      },

      isPending: (taskId) => get().pending.has(taskId) || get().committing.has(taskId),

      isCollapsing: (taskId) => get().collapsing.has(taskId),

      getAction: (taskId) =>
        get().pending.get(taskId)?.action ?? get().committing.get(taskId),

      flushAll: async () => {
        const { pending } = get()
        if (pending.size === 0) return

        // Clear all timers
        for (const [, entry] of pending) {
          clearTimeout(entry.timer)
        }
        const batch = [...pending].map(([id, entry]) => [id, entry.action] as const)
        set({ pending: new Map() })

        // Execute all pending completions immediately
        for (const [id, action] of batch) await writeStatus(id, action)
      },
    }
  },
)
